import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import {
  GameData,
  GraphemeId,
  ScopeAndSequenceLevel,
  SentenceId,
  WordId,
} from '@/common/types/scopeAndSequence';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getGraphemesByIds,
  getSentencesByIds,
  getWordsByIds,
} from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { getNextGame } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { getMixedGameItems } from '@/game/gameHelpers';
import WorldNode from '@/game/prefabs/World/WorldNode';
import Minigame from '@/game/scenes/Minigame';

/*
 *  Game Manager listens to in-game events and manages the state of the GameInstance.
 */
export class GameManager {
  static _instance?: GameManager;

  constructor() {
    if (GameManager.instance !== undefined) {
      return;
    }
    GameManager._instance = this;

    // Event listeners.
    // These need to be registered directly to the event bus because they are not part of a scene.
    EventBus.on(GAME_EVENTS.MINIGAME_STARTED, this.handleGameStarted, this);
    EventBus.on(
      GAME_EVENTS.PLAYER_VISITED_NODE,
      this.onPlayerVisitedLevelNode,
      this,
    );
    EventBus.on(
      GAME_EVENTS.COMPLETED_CURRENT_LEVEL,
      this.handleCompleteCurrentLevel,
      this,
    );
    EventBus.on(
      GAME_EVENTS.PLAYER_CONFIRMED_QUIT_MAIN_LEVEL,
      this.handleQuit,
      this,
    );
    EventBus.on(
      GAME_EVENTS.PLAYER_REQUESTED_COMPLETE_ALL_NODES,
      this.handleAutoCompleteAllNodes,
      this,
    );
    EventBus.on(
      GAME_EVENTS.FINISHED_NODE_COMPLETE_FEEDBACK,
      this.handleNodeCompleteFeedback,
      this,
    );
    EventBus.on(
      GAME_EVENTS.FINISHED_NODE_COMPLETE_FEEDBACK,
      this.handleNodeCompleteFeedback,
      this,
    );
    EventBus.on(GAME_EVENTS.STUDENT_LEVEL_UP, this.handleStudentLevelUp, this);
    EventBus.on(GAME_EVENTS.START_GAME, this.handleStartGame, this);
    EventBus.on(
      GAME_EVENTS.COMPLETED_ASSESSMENT,
      this.handleCompletedAssessment,
      this,
    );
  }

  static get instance(): GameManager {
    return GameManager._instance!;
  }

  static destroyGameManager() {
    GameManager._instance = undefined;
  }

  onPlayerVisitedLevelNode(node: WorldNode) {
    // Ensure data has been set for the games.
    this.setBaseGameData();

    // Update the game map to reflect the player's position.
    GameInstance.instance.updateGameMap({
      playerPosition: { x: node.x, y: node.y },
      currentNodeIndex: node.index!,
      isVisitingNode: true,
    });

    const nextGame = getNextGame();
    // If a game is found that hasn't been completed, dispatch and navigate
    //this ux is kinda shite
    if (nextGame) {
      GameInstance.instance.updateGeneralGameState({
        currentGame: nextGame,
      });

      // Start up the game scene.
      getSceneManager()?.launchScene('Minigame', Minigame, {
        type: 'gameplay',
        data: { node },
      });
    } else {
      console.log('No more games left to complete at this level');
    }
  }

  async autoCompleteAllNodes() {
    const { enabledGames, completedPlaythroughs, gamesProgress } =
      GameInstance.instance.getActiveLevelProgress();

    const updatedGamesProgress = { ...gamesProgress };
    enabledGames.forEach((game, i) => {
      updatedGamesProgress[game] = {
        ...updatedGamesProgress[game],
        timesPlayed: completedPlaythroughs + 1,
        timesMastered: completedPlaythroughs + 1,
        currentHighestMastery: 100,
        completedAtNodeIndex: i,
      };
    });

    if (GameInstance.instance.getShouldPlayCustomLevel()) {
      await GameInstance.instance.updateCustomLevelProgress({
        gamesProgress: updatedGamesProgress,
      });
      return;
    }

    const student = getStudent();
    if (!student.id) return;
    const levelIdx = student.level - 1;
    const updatedStudent = structuredClone(student);
    updatedStudent.levelsProgress = [
      ...updatedStudent.levelsProgress.slice(0, levelIdx),
      {
        ...updatedStudent.levelsProgress[levelIdx],
        gamesProgress: updatedGamesProgress,
      },
      ...updatedStudent.levelsProgress.slice(levelIdx + 1),
    ];
    GameInstance.instance.updateStudent(updatedStudent);
  }

  setBaseGameData() {
    const student = getStudent();
    const customLevel = GameInstance.instance.customLevel;

    if (customLevel && GameInstance.instance.getShouldPlayCustomLevel()) {
      const gameData: Partial<GameData> = {};
      for (const [game, data] of Object.entries(customLevel)) {
        if (
          !data ||
          typeof data !== 'object' ||
          !('enabled' in data) ||
          !data.enabled
        )
          continue;
        const { items } = data as { enabled: boolean; items: string[] };
        switch (game) {
          case 'mysticMatch':
            gameData[game] = getGraphemesByIds(items as GraphemeId[]);
            break;
          case 'wordWeaver':
          case 'phonemeForge':
          case 'morphMaster':
          case 'wordSums':
            gameData[game] = getWordsByIds(items as WordId[]);
            break;
          case 'sentenceBuilder':
            gameData[game] = getSentencesByIds(items as SentenceId[]);
            break;
        }
      }
      GameInstance.instance.updateGeneralGameState({ gameData });
      return;
    }

    const currentLevel = student.level;
    const scopeAndSequenceData =
      scopeAndSequence.levels as ScopeAndSequenceLevel[];
    const currentLevelData = scopeAndSequenceData[currentLevel - 1];
    const itemLimit = student.settings.wordLimit;

    const gameData: Partial<GameData> = {};

    for (const game of currentLevelData.games) {
      // Todo: make ratio configurable
      const newItemRatio = 0.6;
      let itemIds: string[] = [];
      switch (game) {
        case 'mysticMatch':
          itemIds = getMixedGameItems<GraphemeId>(
            'newGraphemes',
            currentLevel,
            itemLimit,
            newItemRatio,
          );
          gameData[game] = getGraphemesByIds(itemIds as GraphemeId[]);
          break;
        case 'wordWeaver':
          itemIds = getMixedGameItems<WordId>(
            'wordSet',
            currentLevel,
            itemLimit,
            newItemRatio,
          );
          gameData[game] = getWordsByIds(itemIds as WordId[]);
          break;
        case 'phonemeForge':
          const wordChain = currentLevelData.wordChain;
          if (wordChain.length > itemLimit) {
            // We can shift the chain wordChain.length - itemLimit times
            // to pick a random starting point and still have itemLimit items.
            const maxShift = wordChain.length - itemLimit;
            const shift = Math.floor(Math.random() * (maxShift + 1));
            itemIds = wordChain.slice(shift, shift + itemLimit);
          } else {
            // Not enough items to pick a random starting point, so we just pick
            // the whole word chain.
            itemIds = wordChain;
          }
          gameData[game] = getWordsByIds(itemIds as WordId[]);
          break;
        case 'morphMaster':
          gameData[game] = getWordsByIds(currentLevelData.morphemeChain);
          break;
        case 'trickyWords':
          itemIds = getMixedGameItems<WordId>(
            'trickyWords',
            currentLevel,
            itemLimit,
            newItemRatio,
          );
          gameData[game] = getWordsByIds(itemIds as WordId[]);
          break;
        case 'sentenceBuilder':
          itemIds = getMixedGameItems<SentenceId>(
            'sentences',
            currentLevel,
            itemLimit,
            newItemRatio,
          );
          gameData[game] = getSentencesByIds(itemIds as SentenceId[]);
          break;
        case 'wordRifts':
          itemIds = getMixedGameItems<WordId>(
            'wordSet',
            currentLevel,
            itemLimit,
            newItemRatio,
          );
          gameData[game] = getWordsByIds(itemIds as WordId[]);
          break;
        case 'wordSums':
          itemIds = getMixedGameItems<WordId>(
            'wordSet',
            currentLevel,
            itemLimit,
            newItemRatio,
            true, // Only include multi-morpheme words
          );
          gameData[game] = getWordsByIds(itemIds as WordId[]);
          break;
        default:
          console.error('Unknown game:', game);
          continue;
      }
    }
    GameInstance.instance.updateGeneralGameState({
      gameData,
    });
  }

  // Handlers.
  handleGameStarted(node: WorldNode) {
    this.onPlayerVisitedLevelNode(node);
  }

  async handleCompleteCurrentLevel() {
    await GameInstance.instance.recordPlaythroughCompleted();
    EventBus.emit(GAME_EVENTS.GENERAL_GAME_STATE_UPDATED_AFTER_LEVEL_COMPLETE);
  }

  // Fires a mixpanel event when the player quits the game.
  async handleQuit() {
    const currentGame = GameInstance.instance.generalGameState.currentGame;
    const organisationId = GameInstance.instance.student.organisationId;
    const currentSession = GameInstance.instance.session.currentSession;
    trackMixpanelEvent(`Learner quit ${currentGame}`, {
      activityName: currentGame,
      organisationId,
      sessionId: currentSession?.id,
    });
  }

  async handleAutoCompleteAllNodes() {
    await this.autoCompleteAllNodes();
  }

  handleNodeCompleteFeedback() {
    GameInstance.instance.updateGeneralGameState({
      lastCompletedGame: '',
    });
  }

  handleStudentLevelUp() {
    this.setBaseGameData();
  }

  handleStartGame() {
    this.setBaseGameData();
  }

  handleCompletedAssessment() {
    this.setBaseGameData();
  }
}
